import React, {
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { Container as DraggableContainer } from 'react-smooth-dnd';
import uuid from 'uuid/v4';
import {
  MenuItem,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';

import { removeSpaces } from '../../../../../utils/string';
import { CoachingActivity } from '../../../../../utils/log';
import useLogger from '../../../../../hooks/useLogger';
import EmptyView from '../../../../components/EmptyView';
import CustomizationContext, { withCustomizationContextReady } from '../../../../context/CustomizationContext';
import ManageClientContext from '../../../../context/ManageClientContext';
import UserContext from '../../../../../context/UserContext';
import FirebaseContext from '../../../../../context/FirebaseContext';
import useToast from '../../../../hooks/useToast';
import AppCustomHabit from '../../../../Model/AppCustomHabit';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import InputLabel from '../../../../../components/v2/InputLabel';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import HabitItem from './components/HabitItem';
import HabitIconPicker from './components/HabitIconPicker';
import { HabitIconCatalogContextProvider } from './context';
import { getIconComponent } from './habitIcons';
import useHabitIconSuggestion, { HabitIconSuggestionStatus } from './hooks/useHabitIconSuggestion';
import { MAX_HABIT_NAME_LENGTH } from './constants';

import texts from './texts.json';
import {
  Container,
  Title,
  Description,
  InputContainer,
  StyledHabitInput,
  StyledButton,
  StyledList,
  StyledSelectField,
} from './styles';

const DEFAULT_TAG = 'default';
const SUGGESTION_DEBOUNCE_MS = 650;

const HabitCustomization = () => {
  const { customHabitCollection } = useContext(CustomizationContext);
  const { firebase } = useContext(FirebaseContext);
  const { remote } = firebase;
  const [selectedTag, setSelectedTag] = useState(DEFAULT_TAG);
  const [selectedHabitDoc, setSelectedHabitDoc] = useState({});
  const [newHabit, setNewHabit] = useState('');
  const [habitList, setHabitList] = useState([]);
  const [habitsDefinition, setHabitsDefinition] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingHabit, setIsAddingHabit] = useState(false);
  const [habitToDelete, setHabitToDelete] = useState(null);
  const [newHabitIconPickerAnchorEl, setNewHabitIconPickerAnchorEl] = useState(null);
  const newHabitIconButtonRef = useRef(null);
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();
  const isComponentMountedRef = useComponentMounted();
  const {
    userDoc: {
      id: coachId,
    },
  } = useContext(UserContext);

  const {
    clientTagsDocs,
  } = useContext(ManageClientContext);

  const clientTagsList = useMemo(() => ([
    {
      id: DEFAULT_TAG,
      value: DEFAULT_TAG,
      label: texts.defaultTagLabel,
    },
    ...clientTagsDocs.map((doc) => ({
      id: doc.id,
      value: doc.tag,
      label: doc.tag,
    })),
  ]), [clientTagsDocs]);

  useEffect(() => {
    if (isComponentMountedRef) {
      const currentHabitDoc = customHabitCollection.docs.find((doc) => doc.tag === selectedTag);
      if (currentHabitDoc) {
        setSelectedHabitDoc(currentHabitDoc);
        setHabitList(currentHabitDoc.habits);
        setHabitsDefinition(currentHabitDoc.habitsDefinition);
      } else {
        setSelectedHabitDoc({});
        setHabitList([]);
        setHabitsDefinition({});
      }
    }
  }, [
    customHabitCollection,
    isComponentMountedRef,
    selectedTag,
    customHabitCollection.docs.length,
  ]);

  const suggestIcon = useCallback(async (habitName, excludeIconIds = []) => {
    try {
      const response = await remote('habits-suggestHabitIcon', { habitName, excludeIconIds });
      if (!response.ok) {
        return null;
      }
      const { suggestedIconId } = await response.json();
      return suggestedIconId ?? null;
    } catch {
      return null;
    }
  }, [remote]);

  const usedIconIds = useMemo(
    () => Object.values(habitsDefinition || {})
      .filter((habit) => habit?.active)
      .map((habit) => habit?.iconId)
      .filter(Boolean),
    [habitsDefinition],
  );

  const {
    handleManualIconChange,
    handleNameChangeStart,
    iconId: newHabitIconId,
    suggestionStatus,
  } = useHabitIconSuggestion({
    debounceMs: SUGGESTION_DEBOUNCE_MS,
    name: newHabit,
    onSuggestionUnavailable: () => {},
    suggestIcon,
    usedIconIds,
  });

  const isSuggestingNewHabitIcon = suggestionStatus === HabitIconSuggestionStatus.LOADING;
  const needsAutoSuggestedIconRefresh = suggestionStatus === HabitIconSuggestionStatus.STALE;

  const updateHabitDoc = useCallback(async (updatedHabitList, updatedHabitsDefinition) => {
    setIsLoading(true);
    try {
      if (!selectedHabitDoc.habits) {
        // Create new document for tag.
        const newDoc = await AppCustomHabit.addDoc({
          habits: updatedHabitList,
          habitsDefinition: updatedHabitsDefinition,
          createdAt: new Date(),
          tag: selectedTag,
          coachId,
        });
        setSelectedHabitDoc(newDoc);
      } else if (updatedHabitList.length === 0) {
        // Delete existing document if all habits are deleted.
        await selectedHabitDoc.deleteDoc();
        setSelectedHabitDoc({});
      } else {
        // Update existing document.
        selectedHabitDoc.setHabitsDefinition(updatedHabitsDefinition);
        selectedHabitDoc.setHabits(updatedHabitList);
      }
      setHabitList(updatedHabitList);
      setHabitsDefinition(updatedHabitsDefinition);
      logCoachingActivity(CoachingActivity.UPDATED_HABIT_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  }, [
    coachId,
    selectedHabitDoc,
    logCoachingActivity,
    selectedTag,
  ]);

  const addNewHabit = useCallback(async () => {
    if (!newHabit) return;

    let newHabitId = uuid();
    let oldHabitObject = null;
    let isDuplicate = false;
    let newHabitList = null;

    if (habitsDefinition) {
      Object.keys(habitsDefinition).forEach((habit) => {
        if (habitsDefinition[habit]?.text && removeSpaces(habitsDefinition[habit].text) === removeSpaces(newHabit)) {
          oldHabitObject = habitsDefinition[habit];
          // bring back the deleted habit if there is any by using the old id
          newHabitId = oldHabitObject.id;

          if (habitsDefinition[habit].active) {
            isDuplicate = true;
          }
        }
      });

      if (isDuplicate) {
        showToast(texts.duplicateHabit, { warning: true });
        return;
      }
      newHabitList = [...habitList];
      if (!habitList.includes(newHabitId)) {
        newHabitList.push(newHabitId);
      }
    } else {
      newHabitList = [newHabitId];
    }

    if (!newHabitIconId) {
      showToast('Pick an icon before adding the habit.', { warning: true });
      return;
    }

    setIsAddingHabit(true);

    let newHabitsDefinition = null;
    if (habitsDefinition && oldHabitObject) {
      newHabitsDefinition = {
        ...habitsDefinition,
        [oldHabitObject.id]: {
          ...habitsDefinition[oldHabitObject.id],
          active: true,
          iconId: newHabitIconId,
        },
      };
    } else {
      newHabitsDefinition = {
        ...habitsDefinition,
        [newHabitId]: {
          text: newHabit,
          id: newHabitId,
          active: true,
          iconId: newHabitIconId,
        },
      };
    }

    await updateHabitDoc(newHabitList, newHabitsDefinition);

    if (isComponentMountedRef.current) {
      setNewHabit('');
      setIsAddingHabit(false);
    }
  }, [
    newHabit,
    newHabitIconId,
    updateHabitDoc,
    habitList,
    habitsDefinition,
    showToast,
    isComponentMountedRef,
  ]);

  // move position of array element after drag and drop
  const move = (array, from, to) => {
    array.splice(to, 0, array.splice(from, 1)[0]);
    return [...array];
  };

  const onDrop = useCallback(({ removedIndex, addedIndex }) => {
    if (habitList) {
      const newArray = move([...habitList], removedIndex, addedIndex);
      updateHabitDoc(newArray, habitsDefinition);
    }
  }, [updateHabitDoc, habitList, habitsDefinition]);

  const handleConfirmDelete = useCallback(() => {
    if (habitList && habitsDefinition && habitToDelete !== null) {
      const newArray = [...habitList];
      const removedHabit = newArray.splice(habitToDelete, 1)[0];

      const newDefinition = { ...habitsDefinition };
      delete newDefinition[removedHabit];
      updateHabitDoc(newArray, newDefinition);
    }
    setHabitToDelete(null);
  }, [
    updateHabitDoc,
    habitList,
    habitsDefinition,
    habitToDelete,
  ]);

  const handleEdit = useCallback((editedIndex, editedText, iconId) => {
    if (habitList && habitsDefinition) {
      const editedHabitId = habitList[editedIndex];
      const newDefinition = { ...habitsDefinition };
      newDefinition[editedHabitId].text = editedText;
      newDefinition[editedHabitId].iconId = iconId ?? newDefinition[editedHabitId].iconId ?? null;
      updateHabitDoc(habitList, newDefinition);
    }
  }, [
    updateHabitDoc,
    habitList,
    habitsDefinition,
  ]);

  const handleIconChange = useCallback((habitId, iconId) => {
    if (habitList && habitsDefinition) {
      const newDef = {
        ...habitsDefinition,
        [habitId]: { ...habitsDefinition[habitId], iconId },
      };
      updateHabitDoc(habitList, newDef);
    }
  }, [habitList, habitsDefinition, updateHabitDoc]);

  const handleHide = useCallback((hiddenIndex) => {
    if (habitList && habitsDefinition) {
      const hiddenHabit = habitList[hiddenIndex];
      const newDefinition = { ...habitsDefinition };
      newDefinition[hiddenHabit].active = !newDefinition[hiddenHabit].active;
      updateHabitDoc(habitList, newDefinition);
    }
  }, [
    updateHabitDoc,
    habitList,
    habitsDefinition,
  ]);

  const NewHabitIcon = useMemo(() => getIconComponent(newHabitIconId), [newHabitIconId]);

  return (
    <HabitIconCatalogContextProvider>
      <Container>
        <Title>{texts.title}</Title>
        <Description>{texts.description}</Description>
        <InputLabel>{texts.clientTag}</InputLabel>
        <StyledSelectField
          value={selectedTag}
          onChange={(e) => setSelectedTag(e.target.value)}
        >
          {clientTagsList.map((tag) => (
            <MenuItem key={tag.id} value={tag.value}>
              {tag.label}
            </MenuItem>
          ))}
        </StyledSelectField>
        <Alert severity="info">{texts.tagPriorityNote}</Alert>
        <InputContainer>
          <IconButton
            ref={newHabitIconButtonRef}
            size="small"
            onClick={() => setNewHabitIconPickerAnchorEl(newHabitIconButtonRef.current)}
            sx={{
              mr: 1,
              flexShrink: 0,
              width: 34,
              height: 34,
              alignSelf: 'center',
            }}
          >
            {isSuggestingNewHabitIcon
              ? <CircularProgress size={16} />
              : <NewHabitIcon size={20} />}
          </IconButton>
          <StyledHabitInput
            variant="outlined"
            label={texts.newHabit}
            value={newHabit}
            onChange={(evt) => {
              handleNameChangeStart();
              setNewHabit(evt.target.value);
            }}
            inputProps={{ maxLength: MAX_HABIT_NAME_LENGTH }}
          />
          <StyledButton
            variant="contained"
            onClick={addNewHabit}
            loading={isAddingHabit}
            disabled={isAddingHabit || !newHabitIconId || isSuggestingNewHabitIcon || needsAutoSuggestedIconRefresh}
          >
            {texts.addHabit}
          </StyledButton>
          <HabitIconPicker
            anchorEl={newHabitIconPickerAnchorEl}
            selectedIconId={newHabitIconId}
            usedIconIds={usedIconIds}
            onChange={(iconId) => {
              handleManualIconChange(iconId);
              setNewHabitIconPickerAnchorEl(null);
            }}
            onClose={() => setNewHabitIconPickerAnchorEl(null)}
          />
        </InputContainer>
        <Description>{texts.habits}</Description>
        <StyledList>
          <DraggableContainer dragHandleSelector=".drag-handle" lockAxis="y" onDrop={onDrop}>
            {habitList.length > 0 ? (
              habitList.map((habitId, index) => {
                const habit = habitsDefinition?.[habitId];
                if (!habit) return null;
                return (
                  <HabitItem
                    index={index}
                    handleDelete={() => setHabitToDelete(index)}
                    habitText={habit.text}
                    key={habitId}
                    handleEdit={handleEdit}
                    handleHide={handleHide}
                    isHidden={!habit.active}
                    iconId={habit.iconId}
                    onIconChange={(iconId) => handleIconChange(habitId, iconId)}
                    usedIconIds={usedIconIds}
                  />
                );
              })
            ) : <EmptyView content={selectedTag === DEFAULT_TAG ? texts.noDefaultHabits : texts.noCustomHabits} />}

          </DraggableContainer>
        </StyledList>
        <LoadingOverlay isLoading={isLoading} />
        {!!habitToDelete && (
          <ConfirmDialog
            isOpen={!!habitToDelete}
            onConfirm={handleConfirmDelete}
            onCancel={() => setHabitToDelete(null)}
            dialogTexts={{
              title: texts.deleteHabitContent,
              content: `${habitsDefinition[habitList[habitToDelete]]?.text || ''}`,
            }}
          />
        )}
      </Container>
    </HabitIconCatalogContextProvider>
  );
};

export default compose(
  withCustomizationContextReady,
  observer,
)(HabitCustomization);
